import { SubscribedHandler } from '#/websocket/useWsInstance';
import { FailedResp } from '@paradigm/api/src';

export interface WebSocketStatus {
  readonly error: string;
  readonly connected: boolean;
  readonly subscribed: boolean;
  readonly subscribing: boolean;
}

export type TeardownFunc = () => void;

export type WebSocketStatusHandler = (status: Partial<WebSocketStatus>) => void;

export interface WebSocketRfqService<WS> {
  ws: WS;
  close(): void;
  /**
   * Start web socket connection if not currently running.
   *
   * ATTENTION! Remember to `close()` when finished.
   */
  startWebSocket(handler?: SubscribedHandler): TeardownFunc;
  subscribeWebSocketStatus(handler: WebSocketStatusHandler): TeardownFunc;
}

export class CountMap<T> {
  private map = new Map<T, number>();

  get(key: T) {
    return this.map.get(key) ?? 0;
  }

  increment(key: T) {
    this.map.set(key, this.get(key) + 1);
  }

  decrement(key: T) {
    const currentCount = this.get(key);
    if (currentCount === 0) return;

    if (currentCount === 1) {
      this.map.delete(key);
      return;
    }

    this.map.set(key, currentCount - 1);
  }

  /**
   * Return iterable of keys in the count map.
   *
   * Note that keys decremented to 0 are removed, so this is effectively
   * listing all keys with count greater than 0.
   */
  keys() {
    return this.map.keys();
  }

  clear() {
    this.map.clear();
  }
}

export const createFailedResp = (errorMessage: string): FailedResp => ({
  ok: false,
  status: 0,
  message: errorMessage,
  error: errorMessage,
  data: {},
});

export interface WebSocketHandlersArgs<T> {
  readonly onError: (
    message: string,
    errorDetails?: string,
    errorType?: 'trade' | 'other',
  ) => void;
  readonly onTradeCompleted: (trade: T) => void;
}
